import { useTranslation } from 'react-i18next'
import { softBadgeColors } from '@structural-climbing/ui'
import { gradeDisplay, type Discipline } from '@structural-climbing/validation'
import { useGymGradeConfig } from '../gym/useGymGradeConfig'
import type { CompetitionRoute } from './types'

interface Props {
  route: CompetitionRoute
  apiUrl: string
  discipline: Discipline
  onClick: () => void
}

/**
 * Adapted from the member Routes page row. The differences are the point of it:
 * the competition label ("B1") leads, and the "N tops" count is gone, because a
 * competition does not show who else has climbed a route.
 */
export function CompetitionRouteRow({ route, apiUrl, discipline, onClick }: Props) {
  const { t } = useTranslation()
  const gradeConfig = useGymGradeConfig()
  // A route set for the competition has no grade: its label is what names it.
  const grade = route.grade === null ? null : gradeDisplay(gradeConfig, route.grade, discipline)
  const badge = grade ? softBadgeColors(grade.color) : null
  // Two separate reasons a route takes no more tops: it came off the wall, or
  // its round shut. They read identically, so they share the styling.
  const inactive = route.removed || route.closed

  return (
    <div
      className={`flex items-center gap-3 rounded-xl border border-divider bg-content1 p-3 ${
        inactive ? 'opacity-60' : 'cursor-pointer active:opacity-70'
      }`}
      onClick={inactive ? undefined : onClick}
    >
      {route.image_r2_key ? (
        <img
          src={`${apiUrl}/climbing-routes/${route.id}/image?size=thumb`}
          alt={grade ? t('routes.gradeBadge', { grade: grade.label }) : (route.label ?? '')}
          className="h-12 w-12 shrink-0 rounded-lg object-cover"
          loading="lazy"
        />
      ) : (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-content2 text-xl text-foreground/30">
          ⛰
        </div>
      )}

      <div className="flex min-w-0 flex-1 items-center gap-2">
        {route.label && (
          <span className="shrink-0 rounded bg-foreground/10 px-2 py-0.5 text-sm font-bold">
            {route.label}
          </span>
        )}
        <span
          className="h-4 w-4 shrink-0 rounded-full border border-foreground/10"
          style={{ backgroundColor: route.color ?? '#9ca3af' }}
        />
        {grade && badge && (
          <span
            data-testid="grade-badge"
            className="rounded px-2 py-0.5 text-sm font-semibold"
            style={{ backgroundColor: badge.bg, color: badge.fg }}
          >
            {grade.label}
          </span>
        )}
        {route.removed ? (
          <span className="rounded bg-default-200 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-foreground/60">
            {t('competitions.routeRemoved')}
          </span>
        ) : (
          route.closed && (
            <span className="rounded bg-default-200 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-foreground/60">
              {t('competitions.routeRoundClosed')}
            </span>
          )
        )}
        {route.has_ascent && (
          <span
            data-testid="climbed-badge"
            className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-primary"
          >
            {t('routes.climbedBadge')}
          </span>
        )}
      </div>

      {route.has_ascent && route.attempts !== null && (
        <span className="shrink-0 text-xs text-foreground/40">
          {route.attempts === 1
            ? t('competitions.flash')
            : t('competitions.attemptsShort', { count: route.attempts })}
        </span>
      )}
    </div>
  )
}
